import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Api, ApiService} from "../../../../../../../common/services/api.service";
import {ToastService} from "../../../../../../../common/services/toast.service";
import {Modal, ModalService} from "../../../../../../services/modal.service";
import {ReplaySubject} from "rxjs";
import {takeUntil} from "rxjs";
import {StorageService} from "../../../../../../../common/services/storage.service";
import {Base} from "../../../../../../../common/interfaces/base.interfaces";
import {UserService} from "../../../../../../../common/services/user.service";
import {Router} from "@angular/router";
import * as moment from "moment";
import {HttpClient} from "@angular/common/http";
import {FileUploadComponent} from "../../../../../../../common/components/file-upload/file-upload.component";
import {SpinnerService} from "../../../../../../../common/services/spinner.service";
import {AppStateService} from "../../../../../../../common/services/app-state.service";
import {RequestKindFormComponent} from "../../index";
import {Form} from "../../../../../../../common/interfaces/form.interface";
import ISelectOption = Form.ISelectOption;
import {Api3Service} from "../../../../../../../common/services/api3.service";


@Component({
    selector: "section-operations-request-form",
    templateUrl: "form.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class OperationsRequestFormComponent implements OnInit, OnDestroy {

    private destroy$: EventEmitter<boolean> = new EventEmitter(false);

    @Input()
    public data: any;

    @Output()
    public onSubmit: EventEmitter<any> = new EventEmitter<any>();

    @Output()
    public onClose: EventEmitter<null> = new EventEmitter<null>();

    @Output()
    public onMinimize: EventEmitter<null> = new EventEmitter<null>();

    /**
     * Form group / group with controls
     * @type {FormGroup}
     */
    public formGroup: FormGroup = new FormGroup({
        title: new FormControl(null, [Validators.required]),
        description: new FormControl(null, [Validators.required]),
        assignee_id: new FormControl(null, [Validators.required]),
        team_id: new FormControl(null),
        inform_team: new FormControl(false),
        request_kind_id: new FormControl(null, [Validators.required]),
        order_remark_type_id: new FormControl(null, [Validators.required]),
        priority: new FormControl("Low", [Validators.required]),
        status: new FormControl("New"),
        url: new FormControl(window.location.pathname),
        order_id: new FormControl(null),
        timeline_from: new FormControl(null),
        timeline_to: new FormControl(null),
        file_url: new FormControl(null),
        participants: new FormControl([])
    });

    public state: Base.IState;

    public users: ISelectOption[] = [];

    public teams: ISelectOption[] = [];

    public partners: ISelectOption[];

    public remarkTypes: ISelectOption[] = [];

    public selectUserSearch: FormControl = new FormControl(null);

    public usersFiltered: ReplaySubject<any> = new ReplaySubject<any>(1);

    public kinds: ISelectOption[] = [];

    public participants: ISelectOption[] = [];

    public selectParticipantSearch: FormControl = new FormControl(null);

    public participantsFiltered: ISelectOption[] = [];

    public showOrderLink: boolean = false;

    public minFromDate: string = moment().format("YYYY-MM-DD");

    public minTODate: string = moment().add(1, "hour").format("YYYY-MM-DD HH:mm");

    public uploadedFile: ISelectOption[] = [];

    public editorInitialValue: string = null;

    public currentUser: any = null;

    public showRelatedPartners = false;


    public constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private apiService: ApiService,
        private api3Service: Api3Service,
        private toastService: ToastService,
        private modalService: ModalService,
        private storageService: StorageService,
        private userService: UserService,
        private router: Router,
        private http: HttpClient,
        private spinnerService: SpinnerService,
    ) {
        this.currentUser = this.userService.data;
        this.formGroup.controls["participants"].setValue([this.currentUser.id]);
    }

    /**
     * Prepare form data
     */
    private prepareData(): void {

        if (this.data) {
            this.formGroup.patchValue(this.data);
            this.editorInitialValue = this.data.description;

            if (this.data.remark_id) {
                this.formGroup.removeControl("description");
                this.formGroup.updateValueAndValidity();
                this.formGroup.addControl("order_remark_id", new FormControl(this.data.remark_id));
            }
        }

        if (this.state.component === "orders" && this.state.action === "view") {
            this.showOrderLink = true;
            this.formGroup.get("order_id").setValue(this.state.params.id);
        }

        if (this.storageService.get("requestOperations")) {
            this.formGroup.patchValue(this.storageService.get("requestOperations"));
            this.editorInitialValue = this.formGroup.value.description;
        }

        if (this.storageService.get("requestOperationsFiles")) {
            this.uploadedFile = this.storageService.get("requestOperationsFiles");
        }
    }


    /**
     * Get users list
     * @returns {Promise<any>}
     */
    private async getUsers(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.api3Service.request(Api.EMethod.Get,
            `/${this.state.section}/users`, {},
            {
                data_structure: "select"
            });
        if (data) {
            this.users = data;
            this.usersFiltered.next(this.users);
            this.changeDetectorRef.markForCheck();
        }
        this.spinnerService.hide();
    }

    /**
     * Get teams list
     * @returns {Promise<any>}
     */
    private async getTeams(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Get,
            ["team"], {}, {
                data_structure: "select",
                category: "request"
            });
        if (data) {
            this.teams = data;
            this.changeDetectorRef.markForCheck();
        }
        this.spinnerService.hide();
    }

    /**
     * Get partners list
     * @returns {Promise<any>}
     */
    private async getPartners(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Get,
            ["warehouse", "partners"], {}, {data_structure: "select"});
        this.spinnerService.hide();
        if (data) {
            this.partners = data;
            this.changeDetectorRef.markForCheck();
        }
    }


    /**
     * Get list of kinds
     * @returns {Promise<any>}
     */
    private async getKinds(): Promise<any> {
        this.spinnerService.show();
        const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Get,
            ["request", "kind"], {}, {
                category: "operations",
                data_structure: "select",
            });
        if (data) {
            this.kinds = data;
            this.changeDetectorRef.markForCheck();
        }
        this.spinnerService.hide();
    }

    /**
     * Get remark types
     * @returns {Promise<any>}
     */
    private async getRemarkTypes(): Promise<any> {
        const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Get,
            ["remark", "order", "types"]);
        if (data) {
            this.remarkTypes = data;
            this.changeDetectorRef.markForCheck();
        }
    }

    private async getPaticipants(): Promise<any> {
        this.spinnerService.show();
        const [type, slug]: string[] = this.state.section.split("/", 2);
        let path: string[] = [type, "users"];
        if (type === "admin") {
            path = ["user", "list"];
        }
        this.participants = [];
        const {data}: Api.IResponse = await this.apiService.request(Api.EMethod.Get,
            path, {}, {
                data_structure: "select",
            });
        if (data) {
            this.participants.push(...data.filter(item => item.value != this.currentUser.id));
        }
        this.participantsFiltered = this.participants;
        this.changeDetectorRef.markForCheck();
        this.spinnerService.hide();
    }

    public async uploadFile(): Promise<any> {
        const response: Modal.IResponse = await this.modalService.open(FileUploadComponent, {
            url: ["user", "file"],
            accept: [
                "gif", "png", "jpg", "jpeg", "tiff", "doc",
                "docx", "xls", "xlsx", "csv", "ppt", "pptx", "msg", "txt", "pdf", "xlsb"
            ]
        });
        if (response && response.value && response.value.data && response.value.data.path) {
            const uploaded: any = {name: response.value.data.name, url: response.value.data.path};
            this.uploadedFile.push(uploaded);
            this.changeDetectorRef.markForCheck();
        }
    }

    public deleteFile(index: number): void {
        this.uploadedFile.splice(index, 1);
    }

    /**
     * Assign to current user
     */
    public assignToMe(): void {
        const id: number = this.userService.data.id;
        this.formGroup.get("assignee_id").setValue(id);
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Submit form
     */
    public async submit(): Promise<any> {
        this.spinnerService.show();

        const body: any = {...this.formGroup.value};

        body.files = this.uploadedFile;

        if (this.formGroup.value.timeline_from) {
            body.timeline_from = this.formGroup.value.timeline_from.format("YYYY-MM-DD");
        }
        if (this.formGroup.value.timeline_to) {
            body.timeline_to = this.formGroup.value.timeline_to.format("YYYY-MM-DD HH:mm:ss");
        }

        body.type = "request";

        const response: Api.IResponse = await this.apiService.request(Api.EMethod.Post, ["request"], body);

        if (response && response.type as string === "success") {
            this.toastService.show(response.message, response.type as string);
            this.onSubmit.emit(response.data);
        }
        this.spinnerService.hide();

        this.storageService.remove("requestOperations");
        this.storageService.remove("requestOperationsFiles");
    }

    public close(): void {
        this.onClose.emit();
    }

    public minimize(): void {
        this.storageService.set("isRequestMinimized", true);
        this.onMinimize.emit();
    }

    public ngOnInit(): void {

        this.state = AppStateService.getState();

        this.getTeams();
        this.getUsers();
        this.getRemarkTypes();
        this.getKinds();
        this.getPartners();
        this.getPaticipants();

        this.changeDetectorRef.markForCheck();

        if ((this.state && this.state.section.includes("warehouse"))) {
            this.formGroup.addControl("related_partner", new FormControl(null, [Validators.required]));
            this.changeDetectorRef.markForCheck();
            this.getPartners();
            this.showRelatedPartners = true;
        }

        this.selectUserSearch.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value: string): void => {
                if (value) {
                    this.usersFiltered.next(
                        this.users.filter((user: { name: string }): boolean =>
                            user.name.toLowerCase().indexOf(value) > -1)
                    );
                } else {
                    this.usersFiltered.next(this.users);
                }
            });

        this.selectParticipantSearch.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value: string): void => {
                if (value) {
                    this.participantsFiltered =
                        this.participants.filter((user: { name: string }): boolean =>
                            user.name.toLowerCase().indexOf(value) > -1);
                } else {
                    this.participantsFiltered = this.participants;
                }
            });


        this.prepareData();
    }

    public ngOnDestroy(): void {
        this.storageService.set("requestOperations", this.formGroup.value);
        this.storageService.set("requestOperationsFiles", this.uploadedFile);
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
